import { CONTRACT_KIND_LABELS, formatDate, formatUah, type RegisterStatus } from '@hr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  BanknoteIcon,
  FileSpreadsheetIcon,
  PlusIcon,
  RefreshCwIcon,
  SendIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { Field } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreview } from '@/features/preview/use-preview';
import { SignedCopy } from '@/features/signed/signed-copy';
import { useRegistry } from '@/features/registry/use-registry';
import { MonthPicker } from '@/features/time/month-picker';
import { REGISTER_STATUS_LABELS, uk } from '@/i18n/uk';
import { currentMonth, monthLabel, shiftMonth } from '@/lib/months';
import { isHr } from '@/lib/roles';
import { errorMessage, trpc } from '@/lib/trpc';

export const Route = createFileRoute('/_app/payroll')({
  validateSearch: z.object({
    project: z.uuid().optional().catch(undefined),
    register: z.uuid().optional().catch(undefined),
  }),
  component: PayrollPage,
});

const STATUS_STYLE: Record<RegisterStatus, string> = {
  draft: '',
  sent: 'border-sky-300 text-sky-800 dark:text-sky-200',
  paid: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
};

function PayrollPage() {
  const t = uk.finance;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canBuild = isHr(me.role);
  const registry = useRegistry();
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const list = useQuery(trpc.payroll.list.queryOptions({ projectId: search.project }));
  const [building, setBuilding] = useState(false);

  return (
    <>
      <PageHeader
        title={t.registers}
        actions={
          canBuild && (
            <Button onClick={() => setBuilding(true)}>
              <PlusIcon /> {t.build}
            </Button>
          )
        }
      />
      <div className="mb-3">
        <Combobox
          className="w-44"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({ value: p.id, label: p.code }))}
          placeholder={uk.registry.project}
          allowClear
        />
      </div>
      {list.isPending ? (
        <Skeleton className="h-48" />
      ) : list.data?.length === 0 ? (
        <EmptyState icon={BanknoteIcon} title={t.emptyRegisters} />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t.number}</th>
                <th className="px-3 py-2 text-left font-medium">{t.month}</th>
                <th className="px-3 py-2 text-left font-medium">{uk.registry.project}</th>
                <th className="px-3 py-2 text-right font-medium">{t.lines}</th>
                <th className="px-3 py-2 text-right font-medium">{t.total}</th>
                <th className="px-3 py-2 text-left font-medium">{t.status}</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t hover:bg-muted/40"
                  onClick={() => setSearch({ register: r.id })}
                >
                  <td className="px-3 py-2 font-medium">{r.number}</td>
                  <td className="px-3 py-2">{monthLabel(r.month.slice(0, 7))}</td>
                  <td className="px-3 py-2">{r.projectCode}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.lines}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {formatUah(r.total)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={STATUS_STYLE[r.status]}>
                      {REGISTER_STATUS_LABELS[r.status]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {building && (
        <BuildDialog
          projectId={search.project}
          onClose={(id) => {
            setBuilding(false);
            if (id) setSearch({ register: id });
          }}
        />
      )}
      <RegisterSheet
        id={search.register ?? null}
        role={me.role}
        onClose={() => setSearch({ register: undefined })}
      />
    </>
  );
}

function BuildDialog({
  projectId,
  onClose,
}: {
  projectId?: string;
  onClose: (id?: string) => void;
}) {
  const t = uk.finance;
  const qc = useQueryClient();
  const registry = useRegistry();
  const [month, setMonth] = useState(shiftMonth(currentMonth(), -1));
  const [project, setProject] = useState<string | null>(projectId ?? null);
  const build = useMutation(
    trpc.payroll.build.mutationOptions({
      onSuccess: (r) => {
        toast.success(t.built);
        onClose(r.id);
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.payroll.pathFilter()),
    }),
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.buildTitle}</DialogTitle>
          <DialogDescription>{t.buildHint}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label={t.month}>
            <MonthPicker value={month} onChange={setMonth} />
          </Field>
          <Field label={uk.registry.project}>
            <Combobox
              value={project}
              onChange={setProject}
              options={(registry.data?.projects ?? []).map((p) => ({
                value: p.id,
                label: p.code,
                hint: p.name,
              }))}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>
            {uk.common.cancel}
          </Button>
          <Button
            disabled={!project || build.isPending}
            onClick={() => build.mutate({ month, projectId: project! })}
          >
            {t.build}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegisterSheet({
  id,
  role,
  onClose,
}: {
  id: string | null;
  role: string;
  onClose: () => void;
}) {
  const t = uk.finance;
  const qc = useQueryClient();
  const query = useQuery({ ...trpc.payroll.get.queryOptions(id ?? ''), enabled: Boolean(id) });
  const openPreview = usePreview();
  const opts = {
    onError: (e: unknown) => toast.error(errorMessage(e)),
    onSettled: () => qc.invalidateQueries(trpc.payroll.pathFilter()),
  };
  const setStatus = useMutation(trpc.payroll.setStatus.mutationOptions(opts));
  const setPaid = useMutation(trpc.payroll.setLinePaid.mutationOptions(opts));
  const build = useMutation(trpc.payroll.build.mutationOptions(opts));
  const r = query.data;
  const hr = role === 'admin' || role === 'recruiter';
  const finance = hr || role === 'accountant';
  return (
    <Sheet open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl">
        <SheetHeader>
          <SheetTitle>{r?.number ?? t.register}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {r && (
              <>
                <Badge variant="outline" className={STATUS_STYLE[r.status]}>
                  {REGISTER_STATUS_LABELS[r.status]}
                </Badge>
                {r.projectCode} · {monthLabel(r.month.slice(0, 7))}
              </>
            )}
          </SheetDescription>
        </SheetHeader>
        {!r ? (
          <Skeleton className="mx-4 h-64" />
        ) : (
          <div className="space-y-3 px-4 pb-4">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => openPreview({ url: `/api/registers/${r.id}/export?format=xlsx` })}
              >
                <FileSpreadsheetIcon /> {t.exportXlsx}
              </Button>
              <Button
                variant="outline"
                onClick={() => openPreview({ url: `/api/registers/${r.id}/export?format=csv` })}
              >
                <FileSpreadsheetIcon /> {t.exportCsv}
              </Button>
              <SignedCopy kind="register" entityId={r.id} title={`Реєстр ${r.number}`} />
              {hr && r.status === 'draft' && (
                <>
                  <Button
                    variant="outline"
                    disabled={build.isPending}
                    onClick={() =>
                      build.mutate({ projectId: r.projectId, month: r.month.slice(0, 7) })
                    }
                  >
                    <RefreshCwIcon /> {t.rebuild}
                  </Button>
                  <Button onClick={() => setStatus.mutate({ id: r.id, status: 'sent' })}>
                    <SendIcon /> {t.send}
                  </Button>
                </>
              )}
              {finance && r.status === 'sent' && (
                <Button onClick={() => setStatus.mutate({ id: r.id, status: 'paid' })}>
                  <BanknoteIcon /> {t.markPaid}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">{t.employee}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t.contract}</th>
                    <th className="px-2 py-1.5 text-left font-medium">{t.formula}</th>
                    <th className="px-2 py-1.5 text-right font-medium">{t.amount}</th>
                    <th className="px-2 py-1.5 text-center font-medium">{t.paid}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.lines.map((l) => (
                    <tr key={l.id} className="border-t align-top">
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{l.name}</div>
                        {l.missingRequisites && (
                          <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                            <AlertTriangleIcon className="size-3" /> {t.missingRequisites}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {l.contractNumber}
                        <div className="text-xs text-muted-foreground">
                          {CONTRACT_KIND_LABELS[l.contractKind]} · {t.basis[l.basis] ?? l.basis}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">{l.formula}</td>
                      <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                        {formatUah(l.amount)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Checkbox
                          checked={Boolean(l.paidAt)}
                          disabled={!finance || r.status === 'draft'}
                          aria-label={t.paid}
                          title={
                            l.paidAt ? formatDate(l.paidAt.toISOString().slice(0, 10)) : undefined
                          }
                          onCheckedChange={(v) => setPaid.mutate({ id: l.id, paid: v === true })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-semibold">
                    <td className="px-2 py-2" colSpan={3}>
                      {t.total}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{formatUah(r.total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
